import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FilterGroup,
  PersonStatus,
  SearchHouseholdRow,
  SearchPeopleRow,
  SearchScope,
  SortField,
  useCreateReport,
  useFilterSearch,
  useReport,
  usePatchReport,
  useSearchCatalog,
} from '../api';
import { EMPTY_GROUP, FilterBuilder } from '../components/FilterBuilder';
import { TagList } from '../components/Tags';
import { printPage } from '../print';
import { formatPhone, telHref } from '../phone';

// ─── Column registry ──────────────────────────────────────────────────────

interface ColumnDef<Row> {
  key: string;
  label: string;
  render: (r: Row) => React.ReactNode;
}

const STATUS_LABEL: Record<PersonStatus, string> = {
  active: 'Active',
  semi_active: 'Semi-Active',
  inactive: 'Inactive',
};

const PEOPLE_COLUMNS: ColumnDef<SearchPeopleRow>[] = [
  {
    key: 'name', label: 'Name',
    render: (r) => (
      <Link className="hover:underline" to={`/people/${r.id}`}>{r.name_full ?? '—'}</Link>
    ),
  },
  { key: 'role', label: 'Role', render: (r) => <span className="text-slate-600 dark:text-slate-400">{r.household_role ?? ''}</span> },
  { key: 'age', label: 'Age', render: (r) => <span className="tabular-nums">{r.age ?? ''}</span> },
  { key: 'sex', label: 'Sex', render: (r) => r.sex ?? '' },
  { key: 'birth_date', label: 'Birthdate', render: (r) => <span className="tabular-nums">{r.birth_date ?? ''}</span> },
  {
    key: 'phone', label: 'Phone',
    render: (r) => r.phone
      ? <a href={telHref(r.phone)} className="tabular-nums hover:underline">{formatPhone(r.phone)}</a>
      : '',
  },
  {
    key: 'email', label: 'Email',
    render: (r) => r.email ? <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a> : '',
  },
  { key: 'address', label: 'Address', render: (r) => stripHtml(r.household_address) },
  { key: 'tags', label: 'Tags', render: (r) => <TagList tags={r.tags} /> },
  {
    key: 'status', label: 'Status',
    render: (r) => (r.status_override ? STATUS_LABEL[r.status_override] : ''),
  },
  {
    key: 'callings', label: 'Calling(s)',
    render: (r) => <span className="text-slate-600 dark:text-slate-400">{r.callings ?? ''}</span>,
  },
  { key: 'occupation', label: 'Occupation', render: (r) => r.occupation ?? '' },
  { key: 'last_interaction', label: 'Last interaction', render: (r) => <LastInteraction at={r.last_interaction_at} /> },
  { key: 'created_at', label: 'Created', render: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span> },
  { key: 'source', label: 'Source', render: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{r.source}</span> },
];

const HOUSEHOLD_COLUMNS: ColumnDef<SearchHouseholdRow>[] = [
  {
    key: 'name', label: 'Name',
    render: (r) => (
      <Link className="hover:underline" to={`/households/${r.id}`}>{r.name_directory ?? '—'}</Link>
    ),
  },
  { key: 'address', label: 'Address', render: (r) => stripHtml(r.formatted_address) },
  { key: 'members', label: 'Members', render: (r) => <span className="text-slate-600 dark:text-slate-400">{r.members ?? ''}</span> },
  { key: 'member_count', label: '#', render: (r) => <span className="tabular-nums">{r.member_count}</span> },
  { key: 'tags', label: 'Tags', render: (r) => <TagList tags={r.tags} /> },
  { key: 'last_interaction', label: 'Last interaction', render: (r) => <LastInteraction at={r.last_interaction_at} /> },
  { key: 'created_at', label: 'Created', render: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span> },
  { key: 'source', label: 'Source', render: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{r.source}</span> },
];

const COLUMN_DEFS = {
  people: PEOPLE_COLUMNS,
  households: HOUSEHOLD_COLUMNS,
} as const;

const DEFAULT_COLUMNS: Record<SearchScope, string[]> = {
  people: ['name', 'role', 'age', 'sex', 'phone', 'email', 'tags', 'last_interaction', 'source'],
  households: ['name', 'address', 'tags', 'last_interaction', 'source'],
};

// ─── Sort ─────────────────────────────────────────────────────────────────

const SORT_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  first_name: 'First name',
  age: 'Age',
  birth_date: 'Birthdate',
  household_role: 'Role',
  status: 'Status',
  occupation: 'Occupation',
  household_address: 'Address',
  address: 'Address',
  callings: 'Calling(s)',
  member_count: 'Member count',
  sector_macro: 'Ward sector',
  sector_micro: 'Walk',
  created_at: 'Created',
  last_interaction_at: 'Last interaction',
};

const DEFAULT_SORT: SortField[] = [{ field: 'name', dir: 'asc' }];

// Reports saved before multi-field sort carry only a legacy sort_by name.
const LEGACY_SORT_MAP: Record<string, SortField[]> = {
  name: [{ field: 'name', dir: 'asc' }],
  age: [{ field: 'age', dir: 'asc' }],
  created_at: [{ field: 'created_at', dir: 'desc' }],
  last_interaction_at: [{ field: 'last_interaction_at', dir: 'desc' }],
  least_recent_interaction: [{ field: 'last_interaction_at', dir: 'asc' }],
};

export default function Search() {
  const [qsParams, setQsParams] = useSearchParams();
  const reportId = qsParams.get('report') || '';
  const navigate = useNavigate();

  // Loaded saved report (when editing)
  const loadedReport = useReport(reportId);

  const [scope, setScope] = useState<SearchScope>('people');
  const [definition, setDefinition] = useState<FilterGroup>(EMPTY_GROUP);
  const [sortSpec, setSortSpec] = useState<SortField[]>(DEFAULT_SORT);
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS.people);

  // When a report is loaded, hydrate the editor
  useEffect(() => {
    if (!reportId) return;
    const r = loadedReport.data;
    if (!r) return;
    setScope(r.scope);
    setDefinition(r.definition ?? EMPTY_GROUP);
    setSortSpec(r.sort ?? LEGACY_SORT_MAP[r.sort_by ?? ''] ?? DEFAULT_SORT);
    setColumns(r.columns ?? DEFAULT_COLUMNS[r.scope]);
  }, [reportId, loadedReport.data]);

  const catalog = useSearchCatalog();
  const results = useFilterSearch(scope, definition, {
    sort: sortSpec,
    limit: 500,
    enabled: !!catalog.data,
  });

  const isEditing = !!reportId && !!loadedReport.data;
  const titleSuffix = isEditing ? ` — ${loadedReport.data?.name ?? ''}` : '';
  const resultCount = results.data?.results.length ?? 0;

  // Where "Map these" points. A saved report travels as its id, so the map can
  // put its name on the sheet — but only while the builder still holds what
  // was saved. Edit the filter and the map gets the tree in front of you, not
  // the version on the server.
  const saved = loadedReport.data?.definition;
  const mapHref = !definition.conditions.length
    ? '/maps'
    : isEditing && JSON.stringify(saved) === JSON.stringify(definition)
      ? `/maps?report=${reportId}`
      : `/maps?scope=${scope}&filter=${encodeURIComponent(JSON.stringify(definition))}`;

  return (
    <div className="space-y-3">
      <PrintHeader
        title={isEditing ? loadedReport.data?.name ?? 'Report' : 'Search results'}
        description={isEditing ? loadedReport.data?.description ?? null : null}
        scope={scope}
        count={resultCount}
      />

      <header className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <div className="flex items-center gap-3">
          <h1 className="text-base font-semibold">Search{titleSuffix}</h1>
          <Link to="/reports" className="text-xs text-slate-500 dark:text-slate-400 hover:underline">
            View reports →
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScopeToggle
            scope={scope}
            onChange={(s) => {
              setScope(s);
              setDefinition(EMPTY_GROUP);
              setSortSpec(DEFAULT_SORT);
              setColumns(DEFAULT_COLUMNS[s]);
            }}
            disabled={isEditing}
          />
          <ColumnsPicker scope={scope} columns={columns} onChange={setColumns} />
          <SaveButtons
            scope={scope}
            definition={definition}
            sortSpec={sortSpec}
            columns={columns}
            isEditing={isEditing}
            reportId={reportId}
            reportName={loadedReport.data?.name ?? ''}
            onSaved={(id) => navigate(`/search?report=${id}`)}
            onCleared={() => {
              setDefinition(EMPTY_GROUP);
              setSortSpec(DEFAULT_SORT);
              setColumns(DEFAULT_COLUMNS[scope]);
              setQsParams({});
            }}
          />
          <Link
            to={mapHref}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            title="Draw these on the ward map"
          >
            Map these
          </Link>
          <button
            onClick={printPage}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            title="Print or save as PDF"
          >
            Print
          </button>
        </div>
      </header>

      <div className="print:hidden">
        {catalog.isLoading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading filter catalog…</p>}
        {catalog.error && <p className="text-sm text-rose-600 dark:text-rose-400">Failed to load filter catalog.</p>}
        {catalog.data && (
          <FilterBuilder
            scope={scope}
            catalog={catalog.data}
            value={definition}
            onChange={setDefinition}
          />
        )}
      </div>

      <SortBuilder
        fields={catalog.data?.sort_fields?.[scope] ?? Object.keys(SORT_FIELD_LABELS)}
        value={sortSpec}
        onChange={setSortSpec}
      />

      <div className="text-xs text-slate-500 dark:text-slate-400 print:hidden">
        {results.isFetching
          ? 'Searching…'
          : results.error
          ? <span className="text-rose-600 dark:text-rose-400">Error: {(results.error as Error).message}</span>
          : `${resultCount} results`}
      </div>

      {scope === 'people' ? (
        <ResultsTable
          rows={(results.data?.results ?? []) as SearchPeopleRow[]}
          defs={PEOPLE_COLUMNS}
          columns={columns}
        />
      ) : (
        <ResultsTable
          rows={(results.data?.results ?? []) as SearchHouseholdRow[]}
          defs={HOUSEHOLD_COLUMNS}
          columns={columns}
        />
      )}
    </div>
  );
}

// Shown only when printing: report title, scope, count, date.
function PrintHeader({
  title,
  description,
  scope,
  count,
}: {
  title: string;
  description: string | null;
  scope: SearchScope;
  count: number;
}) {
  return (
    <div className="hidden print:block">
      <h1 className="text-lg font-semibold">{title}</h1>
      {description && <p className="text-sm text-slate-600">{description}</p>}
      <p className="text-xs text-slate-500">
        {count} {scope === 'people' ? 'people' : 'households'} · printed {new Date().toLocaleDateString()}
      </p>
    </div>
  );
}

function ScopeToggle({
  scope,
  onChange,
  disabled,
}: {
  scope: SearchScope;
  onChange: (s: SearchScope) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-slate-300 dark:border-slate-600 text-xs">
      <button
        onClick={() => onChange('people')}
        disabled={disabled}
        className={`px-2 py-0.5 ${scope === 'people' ? 'bg-slate-900 dark:bg-slate-700 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'} disabled:opacity-50`}
      >
        People
      </button>
      <button
        onClick={() => onChange('households')}
        disabled={disabled}
        className={`px-2 py-0.5 ${scope === 'households' ? 'bg-slate-900 dark:bg-slate-700 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'} disabled:opacity-50`}
      >
        Households
      </button>
    </div>
  );
}

// Multi-level sort: each level is (field, asc|desc); levels apply in order.
function SortBuilder({
  fields,
  value,
  onChange,
}: {
  fields: string[];
  value: SortField[];
  onChange: (v: SortField[]) => void;
}) {
  const update = (i: number, next: Partial<SortField>) => {
    onChange(value.map((s, j) => (j === i ? { ...s, ...next } : s)));
  };
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => {
    const unused = fields.find((f) => !value.some((s) => s.field === f)) ?? fields[0];
    onChange([...value, { field: unused, dir: 'asc' }]);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400 print:hidden">
      <span>Sort</span>
      {value.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-slate-400 dark:text-slate-500">then</span>}
          <select
            value={s.field}
            onChange={(e) => update(i, { field: e.target.value })}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          >
            {fields.map((f) => (
              <option key={f} value={f}>{SORT_FIELD_LABELS[f] ?? f}</option>
            ))}
          </select>
          <select
            value={s.dir}
            onChange={(e) => update(i, { dir: e.target.value as SortField['dir'] })}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
          >
            <option value="asc">↑ asc</option>
            <option value="desc">↓ desc</option>
          </select>
          {value.length > 1 && (
            <button
              onClick={() => remove(i)}
              className="text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
              title="Remove sort level"
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {value.length < 3 && (
        <button
          onClick={add}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          + then by
        </button>
      )}
    </div>
  );
}

// Checkbox dropdown for choosing visible result columns. "Name" is pinned.
function ColumnsPicker({
  scope,
  columns,
  onChange,
}: {
  scope: SearchScope;
  columns: string[];
  onChange: (cols: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const defs = COLUMN_DEFS[scope];

  const toggle = (key: string) => {
    if (columns.includes(key)) {
      onChange(columns.filter((c) => c !== key));
    } else {
      // Keep display order canonical (registry order), not click order.
      const next = new Set([...columns, key]);
      onChange(defs.filter((d) => next.has(d.key)).map((d) => d.key));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        Columns ({columns.length}) ▾
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-48 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 shadow-lg">
          <ul className="space-y-1 text-xs">
            {defs.map((d) => (
              <li key={d.key}>
                <label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={columns.includes(d.key)}
                    disabled={d.key === 'name'}
                    onChange={() => toggle(d.key)}
                  />
                  {d.label}
                </label>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setOpen(false)}
            className="mt-2 w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function SaveButtons({
  scope,
  definition,
  sortSpec,
  columns,
  isEditing,
  reportId,
  reportName,
  onSaved,
  onCleared,
}: {
  scope: SearchScope;
  definition: FilterGroup;
  sortSpec: SortField[];
  columns: string[];
  isEditing: boolean;
  reportId: string;
  reportName: string;
  onSaved: (id: string) => void;
  onCleared: () => void;
}) {
  const create = useCreateReport();
  const patch = usePatchReport();
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const saveExisting = () => {
    if (!reportId) return;
    patch.mutate({
      id: reportId,
      body: { scope, definition, sort: sortSpec, columns },
    });
  };

  const submitNew = () => {
    if (!name.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        scope,
        definition,
        sort: sortSpec,
        columns,
      },
      {
        onSuccess: (res) => {
          setName(''); setDescription(''); setShowSaveAs(false);
          onSaved(res.id);
        },
      },
    );
  };

  if (showSaveAs) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Report name"
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="w-48 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
        />
        <button
          onClick={submitNew}
          disabled={create.isPending || !name.trim()}
          className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
        >
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => { setShowSaveAs(false); setName(''); setDescription(''); }}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {isEditing && (
        <>
          <button
            onClick={saveExisting}
            disabled={patch.isPending}
            className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
            title={`Update "${reportName}"`}
          >
            {patch.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onCleared}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
          >
            New search
          </button>
        </>
      )}
      <button
        onClick={() => setShowSaveAs(true)}
        className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        Save as report…
      </button>
    </div>
  );
}

function ResultsTable<Row extends { id: string }>({
  rows,
  defs,
  columns,
}: {
  rows: Row[];
  defs: ColumnDef<Row>[];
  columns: string[];
}) {
  const active = columns
    .map((key) => defs.find((d) => d.key === key))
    .filter((d): d is ColumnDef<Row> => !!d);

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase text-slate-500 dark:text-slate-400">
          {active.map((d) => (
            <th key={d.key} className="py-1 pr-2">{d.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800">
            {active.map((d) => (
              <td key={d.key} className="py-1 pr-2">{d.render(r)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Relative age of the last contact, tinted by staleness so the visit queue
// reads at a glance: green ≤30d, amber ≤90d, red beyond, muted never.
function LastInteraction({ at }: { at: string | null }) {
  if (!at) return <span className="text-xs text-slate-400 dark:text-slate-500">never</span>;
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000);
  const label = days < 1 ? 'today' : days < 30 ? `${days}d ago` : days < 365 ? `${Math.floor(days / 30)}mo ago` : `${Math.floor(days / 365)}y ago`;
  const tone = days <= 30 ? 'text-emerald-700 dark:text-emerald-300' : days <= 90 ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300';
  return <span className={`text-xs tabular-nums ${tone}`}>{label}</span>;
}

function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]+>/g, '').trim();
}
